import * as React from 'react';
import { Alert, Box, Button, Chip, FormControl, InputLabel, MenuItem, Select, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import Link from '@mui/material/Link';
import { assignDailyQuestDebug, cancelDailyQuestInstance, getDailyQuestInstances, getDailyQuestTemplates, testDailyQuestEligibility } from '../Api';
import DailyQuestAdminInstanceDto from '../models/dtos/DailyQuestAdminInstanceDto';
import DailyQuestEligibilityDto from '../models/dtos/DailyQuestEligibilityDto';
import DailyQuestTemplateDto from '../models/dtos/DailyQuestTemplateDto';
import DailyQuestDetailsDialog from './DailyQuestDetailsDialog';

function formatDate(value?: string | null) {
    return value ? new Date(value).toLocaleString() : '-';
}

function TaskImplementationDot({ implemented, title }: { implemented: boolean; title: string }) {
    return (
        <Box
            component="span"
            title={title}
            sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: implemented ? 'success.main' : 'grey.400',
                display: 'inline-block',
            }}
        />
    );
}

function localizationPreview(localizations: Record<string, string>): string {
    return localizations.en || Object.values(localizations)[0] || '';
}

export default function UserDailyQuestsTable({ userId }: { userId: number }) {
    const [quests, setQuests] = React.useState<DailyQuestAdminInstanceDto[]>([]);
    const [templates, setTemplates] = React.useState<DailyQuestTemplateDto[]>([]);
    const [selectedTemplateId, setSelectedTemplateId] = React.useState('');
    const [filterRewardClaimed, setFilterRewardClaimed] = React.useState<'' | 'true' | 'false'>('');
    const [selectedQuestId, setSelectedQuestId] = React.useState<string | null>(null);
    const [eligibilityLoading, setEligibilityLoading] = React.useState(false);
    const [eligibilityResult, setEligibilityResult] = React.useState<DailyQuestEligibilityDto | null>(null);
    const [eligibilityError, setEligibilityError] = React.useState<string | null>(null);

    const load = React.useCallback(async () => {
        const [questPage, templateRows] = await Promise.all([
            getDailyQuestInstances(
                userId,
                undefined,
                filterRewardClaimed === '' ? undefined : filterRewardClaimed === 'true',
                0,
                25,
            ),
            getDailyQuestTemplates(true),
        ]);
        setQuests(questPage?.items ?? []);
        setTemplates(templateRows ?? []);
    }, [filterRewardClaimed, userId]);

    React.useEffect(() => {
        load();
    }, [load]);

    const assign = async () => {
        if (!selectedTemplateId) return;
        await assignDailyQuestDebug({ userId, templateId: selectedTemplateId });
        await load();
    };

    const cancel = async (questId: string) => {
        await cancelDailyQuestInstance(questId);
        await load();
    };

    const testEligibility = async () => {
        setEligibilityLoading(true);
        setEligibilityError(null);
        const result = await testDailyQuestEligibility(userId);
        setEligibilityLoading(false);
        if (result.ok && result.data) {
            setEligibilityResult(result.data);
        } else {
            setEligibilityResult(null);
            setEligibilityError(result.error ?? 'Failed to test daily quest eligibility.');
        }
    };

    return (
        <Stack gap={2}>
            <Stack gap={1}>
                <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
                    <Button variant="outlined" onClick={testEligibility} disabled={eligibilityLoading}>
                        {eligibilityLoading ? 'Testing eligibility...' : 'Test eligibility'}
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                        Dry run for SessionStarted using the same checks as getDailyQuest. No new quest is assigned.
                    </Typography>
                </Stack>
                {eligibilityError && <Alert severity="error">{eligibilityError}</Alert>}
                {eligibilityResult && (
                    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                        <Alert severity={eligibilityResult.eligible ? 'success' : 'warning'} sx={{ mb: 2 }}>
                            {eligibilityResult.eligible
                                ? `Eligible. getDailyQuest would assign "${eligibilityResult.template?.name ?? 'unknown template'}".`
                                : `Not eligible. ${eligibilityResult.reason ?? eligibilityResult.reasonCode ?? 'No reason returned.'}`}
                        </Alert>
                        <Stack gap={1}>
                            <Stack direction="row" gap={1} flexWrap="wrap">
                                <Chip size="small" label={`Event: ${eligibilityResult.activationEvent}`} />
                                <Chip size="small" label={`Checked: ${formatDate(eligibilityResult.checkedAt)}`} />
                                {eligibilityResult.reasonCode && <Chip size="small" color="warning" label={eligibilityResult.reasonCode} />}
                                {eligibilityResult.blockingQuestId && (
                                    <Chip size="small" label={`Blocking quest: ${eligibilityResult.blockingQuestId}`} />
                                )}
                                {eligibilityResult.blockingQuestExpiresAt && (
                                    <Chip size="small" label={`Blocks until: ${formatDate(eligibilityResult.blockingQuestExpiresAt)}`} />
                                )}
                            </Stack>
                            {eligibilityResult.template && (
                                <Typography variant="body2">
                                    Template: <strong>{eligibilityResult.template.name}</strong> ({eligibilityResult.template.id}), reward {eligibilityResult.template.rewardBetcoins}
                                </Typography>
                            )}
                            {eligibilityResult.missingRequiredTags.length > 0 && (
                                <Typography variant="body2">
                                    Missing required tags: {eligibilityResult.missingRequiredTags.join(', ')}
                                </Typography>
                            )}
                            {eligibilityResult.userTags.length > 0 && (
                                <Typography variant="body2" color="text.secondary">
                                    User tags: {eligibilityResult.userTags.join(', ')}
                                </Typography>
                            )}
                            {eligibilityResult.tasks.length > 0 && (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Task</TableCell>
                                            <TableCell>Target</TableCell>
                                            <TableCell>Template ref</TableCell>
                                            <TableCell>Implemented</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {eligibilityResult.tasks.map(task => (
                                            <TableRow key={task.poolEntryId}>
                                                <TableCell>
                                                    <Stack spacing={0.25}>
                                                        <span>{task.taskType}</span>
                                                        {localizationPreview(task.nameLocalizations) && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                {localizationPreview(task.nameLocalizations)}
                                                            </Typography>
                                                        )}
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>{task.progressTarget}</TableCell>
                                                <TableCell>{task.userTaskTemplateCode || task.userTaskTemplateId || '-'}</TableCell>
                                                <TableCell>{task.userTaskTemplateImplemented ? 'true' : 'false'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </Stack>
                    </Box>
                )}
            </Stack>
            <Stack direction="row" gap={2} alignItems="center">
                <FormControl sx={{ minWidth: 280 }}>
                    <InputLabel>Template</InputLabel>
                    <Select label="Template" value={selectedTemplateId} onChange={e => setSelectedTemplateId(e.target.value)}>
                        {templates.map(template => <MenuItem key={template.id} value={template.id}>{template.name}</MenuItem>)}
                    </Select>
                </FormControl>
                <Button variant="contained" onClick={assign}>Assign</Button>
                <FormControl sx={{ minWidth: 180 }}>
                    <InputLabel>Reward claimed</InputLabel>
                    <Select label="Reward claimed" value={filterRewardClaimed} onChange={e => setFilterRewardClaimed(e.target.value as '' | 'true' | 'false')}>
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="true">true</MenuItem>
                        <MenuItem value="false">false</MenuItem>
                    </Select>
                </FormControl>
            </Stack>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Template</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Reward claimed</TableCell>
                        <TableCell>Progress</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Expires</TableCell>
                        <TableCell align="right">Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {quests.map(quest => (
                        <TableRow key={quest.id}>
                            <TableCell>
                                <Link
                                    component="button"
                                    type="button"
                                    underline="hover"
                                    onClick={() => setSelectedQuestId(quest.id)}
                                    sx={{ font: 'inherit' }}
                                >
                                    {quest.id}
                                </Link>
                            </TableCell>
                            <TableCell>{quest.templateName}</TableCell>
                            <TableCell>{quest.status}</TableCell>
                            <TableCell>{quest.rewardClaimed ? 'true' : 'false'}</TableCell>
                            <TableCell>
                                <Stack spacing={0.5}>
                                    <span>{quest.tasks.filter(t => t.status === 'COMPLETED').length} / {quest.tasks.length}</span>
                                    <Stack direction="row" spacing={0.5}>
                                        {quest.tasks.map(task => (
                                            <TaskImplementationDot
                                                key={task.poolEntryId}
                                                implemented={task.userTaskTemplateImplemented}
                                                title={`${task.taskType}: ${task.userTaskTemplateImplemented ? 'implemented' : 'not implemented'} in v7`}
                                            />
                                        ))}
                                    </Stack>
                                </Stack>
                            </TableCell>
                            <TableCell>{formatDate(quest.createdAt)}</TableCell>
                            <TableCell>{formatDate(quest.expiresAt)}</TableCell>
                            <TableCell align="right">
                                {quest.status === 'PENDING' && (
                                    <Button
                                        color="error"
                                        onClick={event => {
                                            event.stopPropagation();
                                            void cancel(quest.id);
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                    {quests.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={8}>
                                <Typography variant="body2" color="text.secondary">No daily quests.</Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
            <DailyQuestDetailsDialog
                open={selectedQuestId != null}
                questId={selectedQuestId}
                onClose={() => setSelectedQuestId(null)}
                onChanged={load}
            />
        </Stack>
    );
}
